/**
 * D235 (actors round B2): prompts — an actor ASKING the player something,
 * and the third kind of parked question (`docs/design/actors.md` §4, the
 * half of conversation that needs a mouth; `actors-content.md` §5.1 split
 * it from the topic table as round B2).
 *
 * @remarks
 * A topic is the player asking; a prompt is the actor asking back. The
 * actor speaks `questionText` in its own voice (its declared speaker — a
 * mute actor cannot ask, and the schema says so on the actor), and the
 * question is PARKED beside D37's completion and D79's which-one: it waits
 * at the input line while the player looks around, exactly as the engine's
 * own questions do. The visible options are the chips; a chip submits its
 * label as the player's words (D79's rule — the answer is a command in the
 * history, so the replay is the words).
 *
 * **Answer matching is the reaction table's discipline** (D27): the input,
 * folded, against each option's `label` and `aliases`, whole-input, first
 * match in declared order — never fuzzy, because an answer that "almost"
 * matched a different option is a lie the player did not tell. An input
 * that matches no option is a COMMAND and runs as one; the prompt stays
 * parked. Leaving the room withdraws it, speaking `unansweredNarratives`
 * when the prompt authors them (else the engine's `questionWithdrawn`);
 * `never mind` sets it aside.
 *
 * **An option's `when` decides whether it is OFFERED**, read when the
 * prompt is asked; a prompt whose options all fail their `when` is not
 * asked at all (a question with no answers is not a question). An option
 * must do something — speak, change the world, or lead on — or it is a
 * chip that does nothing, which the schema refuses. `leadsTo` asks the
 * next prompt of the SAME actor in the same turn: that is the
 * back-and-forth. A prompt is asked at most once unless `repeatable`; the
 * ledger of what has been asked is the PLAYER's (Mike's ruling 4,
 * 2026-10-04: what is said stays said — a checkpoint restore keeps it, as
 * it keeps the voice ledger and the spent topics). The loader refuses a
 * `leadsTo` cycle unless every prompt in it is repeatable — a conversation
 * that cannot end is dead content of a new kind.
 *
 * Two doors open a prompt (ruling 6): `talk to <actor>` speaks the
 * greeting and then asks the actor's `opensWith` prompt (sugar), and the
 * `ask_prompt` effect lets a trigger make the actor speak first — "she
 * looks up as you come in". Both desugar to one act, the ask.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { promptIdSchema, promptOptionIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';
import { narrativeLineSchema } from './transcript.js';

/**
 * One answer the player may give — see the module remarks.
 */
export const promptOptionSchema = z
	.strictObject({
		/** Every other way of saying this answer, matched whole against the folded input. Default none: the label alone answers. */
		aliases: z.array(nonBlankStringSchema).readonly().default([]),
		/** Effects folded after the narratives, in authored order — a `use` interaction's fold. Default none. */
		effects: z.array(effectSchema).readonly().default([]),
		/** The chip's text, and the words that answer when typed back. */
		label: nonBlankStringSchema,
		/** The prompt this answer leads on to — asked in the same turn, after the narratives and effects. One of the same actor's prompts (the loader says so). */
		leadsTo: promptIdSchema.optional(),
		/** What is said when this answer is given — ordinary lines, any source. Default none: an answer may only change the world, or only lead on. */
		narratives: z.array(narrativeLineSchema).readonly().default([]),
		optionId: promptOptionIdSchema,
		/** Offered only while this holds, read when the prompt is asked. Absent, always offered. */
		when: conditionSchema.optional(),
	})
	.readonly()
	// An answer that says nothing, changes nothing and leads nowhere is a chip that does nothing (D31's dead-content law).
	.refine((option) => option.narratives.length > 0 || option.effects.length > 0 || option.leadsTo !== undefined, {
		message: 'an option with no narratives, no effects and no leadsTo is a chip that does nothing — give the answer a line, a consequence, or a prompt to lead to',
		path: ['narratives'],
	});

/** One answer a prompt offers — see {@link promptOptionSchema}. */
export type PromptOption = z.infer<typeof promptOptionSchema>;

/**
 * One prompt — see the module remarks.
 */
export const promptDefinitionSchema = z
	.strictObject({
		/** The answers, in the order they are offered and matched. */
		options: z.array(promptOptionSchema).min(1).readonly(),
		promptId: promptIdSchema,
		/** The question, spoken in the actor's voice when it is asked, and the parked question's face. */
		questionText: nonBlankStringSchema,
		/** Asked again whenever a door opens it. Default false: once, in the player's ledger. */
		repeatable: z.boolean().default(false),
		/** Said when the player leaves the room with the question unanswered. Absent, the `questionWithdrawn` slot. */
		unansweredNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
	})
	.readonly()
	.refine((prompt) => new Set(prompt.options.map((option) => option.optionId)).size === prompt.options.length, {
		message: 'a prompt offers the same optionId twice',
		path: ['options'],
	});

/** One authored prompt — see {@link promptDefinitionSchema}. */
export type PromptDefinition = z.infer<typeof promptDefinitionSchema>;

/**
 * An actor's prompts: declared order is the order nothing depends on — a
 * prompt is reached by a door (`opensWith`, `leadsTo`, `ask_prompt`), never
 * by position. `min(1)`: an empty list declares nothing.
 */
export const promptsSchema = z.array(promptDefinitionSchema).min(1).readonly();

/** An actor's prompts — see {@link promptsSchema}. */
export type Prompts = z.infer<typeof promptsSchema>;
